import { data, useLoaderData, useNavigate } from 'react-router'
import { BusinessCard } from '#app/components/business-card.tsx'
import { BusinessPagination } from '#app/components/business-pagination.tsx'
import { BusinessSearch } from '#app/components/business-search.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'

const PAGE_SIZE = 36

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const pageParam = Number(url.searchParams.get('page') || '1')
	const q = url.searchParams.get('q')?.trim() || ''
	const rawPage =
		Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1

	// Fetch cities and categories that have published businesses
	const [cities, categories] = await Promise.all([
		prisma.city.findMany({
			where: {
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.category.findMany({
			where: {
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
	])

	const baseWhere = { status: 'published' as const }
	const where =
		q.length > 0
			? {
					AND: [
						baseWhere,
						{
							OR: [
								{ name: { contains: q, mode: 'insensitive' as const } },
								{ description: { contains: q, mode: 'insensitive' as const } },
								{
									aiDescription: { contains: q, mode: 'insensitive' as const },
								},
							],
						},
					],
				}
			: baseWhere

	const total = await prisma.business.count({ where })

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
	const page = Math.min(rawPage, totalPages)
	const skip = (page - 1) * PAGE_SIZE

	const businesses = await prisma.business.findMany({
		where,
		select: {
			id: true,
			name: true,
			slug: true,
			description: true,
			aiDescription: true,
			phone: true,
			website: true,
			address: true,
			isFeatured: true,
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
		},
		orderBy: [{ isFeatured: 'desc' }, { createdAt: 'desc' }, { name: 'asc' }],
		skip,
		take: PAGE_SIZE,
	})

	return data({
		businesses,
		cities,
		categories,
		page,
		pageSize: PAGE_SIZE,
		total,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Businesses', url: '/businesses' }
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const canonical = `${origin}/businesses`

		const structuredData =
			loaderData.businesses.length > 0
				? [
						buildItemListLd({
							name: 'Businesses in Dallas-Fort Worth',
							items: loaderData.businesses.map((b) => ({
								name: b.name,
								url: `${origin}/businesses/${b.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: 'Businesses in Dallas-Fort Worth - RentDFW.com',
			description:
				'Browse local businesses in the Dallas-Fort Worth area. Find professional services and companies in your area.',
			canonical,
			openGraph: {
				title: 'Businesses in Dallas-Fort Worth - RentDFW.com',
				description:
					'Browse local businesses in the Dallas-Fort Worth area. Find professional services and companies in your area.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Businesses in Dallas-Fort Worth - RentDFW.com',
				description: 'Browse local businesses in the Dallas-Fort Worth area.',
			},
			structuredData,
		}
	},
}

export default function BusinessesIndexPage() {
	const { businesses, cities, categories, page, pageSize, total } =
		useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const totalPages = Math.max(1, Math.ceil(total / pageSize))

	function handleCityChange(value: string) {
		void navigate(`/businesses/city/${value}`)
	}

	function handleCategoryChange(value: string) {
		void navigate(`/businesses/category/${value}`)
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mb-8">
					<div className="mb-4 flex flex-col gap-4">
						<div className="mx-auto max-w-3xl text-center">
							<h1 className="text-4xl font-semibold text-balance">
								Rental Businesses in Dallas-Fort Worth
							</h1>
							<p className="text-muted-foreground mt-2 mb-8 text-xl">
								Discover local rental businesses across the Dallas-Fort Worth
								metroplex. Connect with professional services, retailers, and
								rental providers in your area.
							</p>
						</div>

						<div className="flex flex-col gap-3 md:flex-row">
							<div className="w-full">
								<BusinessSearch total={total} />
							</div>
							{cities.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={cities.map((city) => ({
											label: city.name,
											value: city.slug,
										}))}
										onValueChange={handleCityChange}
										placeholder="Filter by city"
										searchPlaceholder="Search cities..."
										emptyText="No cities found"
									/>
								</div>
							)}
							{categories.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={categories.map((category) => ({
											label: category.name,
											value: category.slug,
										}))}
										onValueChange={handleCategoryChange}
										placeholder="Filter by category"
										searchPlaceholder="Search categories..."
										emptyText="No categories found"
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{total === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Businesses Found</h3>
						<p className="text-muted-foreground">
							No businesses available at the moment. Check back soon!
						</p>
					</div>
				) : (
					<>
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{businesses.map((business) => (
								<BusinessCard key={business.id} business={business} />
							))}
						</div>

						<BusinessPagination currentPage={page} totalPages={totalPages} />
					</>
				)}
			</div>
		</>
	)
}
